import type { DerivedRankDependency } from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/aggregated-metrics";
import type { VizDatasetRow } from "@/lib/ai/visualization/data/schema";

function deriveRankNumeratorValue(args: {
  row: VizDatasetRow;
  rankKey: string;
  denominatorKey: string;
}): number | null {
  const rank = args.row[args.rankKey];
  const denominator = args.row[args.denominatorKey];
  if (typeof rank !== "number" || !Number.isFinite(rank)) return null;
  if (typeof denominator !== "number" || !Number.isFinite(denominator)) {
    return null;
  }
  return (rank - 1) * denominator;
}

/**
 * rank 再集計に必要な `sumOfPosition` 系分子を、tool 出力の rank/impression から補完する。
 *
 * @remarks
 * Search Analytics の moving average rank は可視化 payload に分子が含まれないため、
 * adapter で内部列へ復元して renderer の weighted aggregation 契約を満たす。
 * upstream が将来分子を返すようになった場合は、既存値を優先して上書きしない。
 */
export function attachDerivedRankDependencies(
  rows: VizDatasetRow[],
  dependencies: readonly DerivedRankDependency[],
): VizDatasetRow[] {
  return rows.map((row) => {
    let nextRow = row;

    for (const dependency of dependencies) {
      const existingValue = row[dependency.numeratorKey];
      if (typeof existingValue === "number" && Number.isFinite(existingValue)) {
        continue;
      }

      const derivedValue = deriveRankNumeratorValue({
        row,
        rankKey: dependency.rankKey,
        denominatorKey: dependency.denominatorKey,
      });
      if (derivedValue === null) continue;

      if (nextRow === row) {
        nextRow = { ...row };
      }
      nextRow[dependency.numeratorKey] = derivedValue;
    }

    return nextRow;
  });
}
